// src/component/GroupActivity2026DialogAdmin.js
//
// 「2026 グループ活動 参加可否ダイアログ」の管理画面。
// - 表示ON/OFF
// - 文言（チップ・見出し・本文）
// を Firestore: systemConfig/groupActivity2026Dialog で管理する。

import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Button,
  Stack,
  Radio,
  RadioGroup,
} from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import { formatDeadlineJa } from "./groupActivity2026Format";
import { ensureServerTimeSynced } from "../api/serverTime";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";
import GroupActivity2026Dialog from "./GroupActivity2026Dialog";
import {
  GROUP_ACTIVITY_2026_DEFAULTS,
  GROUP_ACTIVITY_2026_CONFIG_DOC,
  GA2026_AUDIENCE,
} from "./groupActivity2026Defaults";

export default function GroupActivity2026DialogAdmin() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive ?? "#5A35FF";
  const surface = theme.surfaceBg ?? "#FFFFFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";

  const [audience, setAudience] = useState(GROUP_ACTIVITY_2026_DEFAULTS.audience);
  const [enabled, setEnabled] = useState(true);
  const [chipLabel, setChipLabel] = useState(GROUP_ACTIVITY_2026_DEFAULTS.chipLabel);
  const [heading, setHeading] = useState(GROUP_ACTIVITY_2026_DEFAULTS.heading);
  const [body, setBody] = useState(GROUP_ACTIVITY_2026_DEFAULTS.body);

  const [tooltipEnabled, setTooltipEnabled] = useState(
    GROUP_ACTIVITY_2026_DEFAULTS.tooltipEnabled
  );
  const [tooltipText, setTooltipText] = useState(
    GROUP_ACTIVITY_2026_DEFAULTS.tooltipText
  );

  const [deadline, setDeadline] = useState(GROUP_ACTIVITY_2026_DEFAULTS.deadline);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTooltip, setSavingTooltip] = useState(false);
  const [savingDeadline, setSavingDeadline] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "success" });

  // サーバ時刻の同期（プレビューの「期日を過ぎています」表示を正しくするため）
  useEffect(() => {
    ensureServerTimeSynced();
  }, []);

  // 設定ドキュメントを購読
  useEffect(() => {
    const ref = doc(db, "systemConfig", GROUP_ACTIVITY_2026_CONFIG_DOC);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          setAudience(
            data.audience === GA2026_AUDIENCE.ALL
              ? GA2026_AUDIENCE.ALL
              : GA2026_AUDIENCE.ADMIN
          );
          setEnabled(data.enabled !== false); // 未定義は true 扱い
          setChipLabel(data.chipLabel ?? GROUP_ACTIVITY_2026_DEFAULTS.chipLabel);
          setHeading(data.heading ?? GROUP_ACTIVITY_2026_DEFAULTS.heading);
          setBody(data.body ?? GROUP_ACTIVITY_2026_DEFAULTS.body);
          setTooltipEnabled(
            data.tooltipEnabled === true
          );
          setTooltipText(
            data.tooltipText ?? GROUP_ACTIVITY_2026_DEFAULTS.tooltipText
          );
          setDeadline(data.deadline ?? GROUP_ACTIVITY_2026_DEFAULTS.deadline);
        } else {
          setAudience(GROUP_ACTIVITY_2026_DEFAULTS.audience);
          setEnabled(true);
          setChipLabel(GROUP_ACTIVITY_2026_DEFAULTS.chipLabel);
          setHeading(GROUP_ACTIVITY_2026_DEFAULTS.heading);
          setBody(GROUP_ACTIVITY_2026_DEFAULTS.body);
          setTooltipEnabled(GROUP_ACTIVITY_2026_DEFAULTS.tooltipEnabled);
          setTooltipText(GROUP_ACTIVITY_2026_DEFAULTS.tooltipText);
          setDeadline(GROUP_ACTIVITY_2026_DEFAULTS.deadline);
        }
        setLoading(false);
      },
      (err) => {
        console.error("GroupActivity2026DialogAdmin onSnapshot error:", err);
        setLoading(false);
        setSnackbar({
          open: true,
          message: "設定の取得に失敗しました。",
          type: "error",
        });
      }
    );
    return () => unsub();
  }, []);

  const showSnack = useCallback((message, type = "success") => {
    setSnackbar({ open: true, message, type });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const persist = async (patch) => {
    const ref = doc(db, "systemConfig", GROUP_ACTIVITY_2026_CONFIG_DOC);
    await setDoc(ref, patch, { merge: true });
  };

  const handleAudienceChange = async (event) => {
    const value =
      event.target.value === GA2026_AUDIENCE.ALL
        ? GA2026_AUDIENCE.ALL
        : GA2026_AUDIENCE.ADMIN;
    setAudience(value);
    try {
      await persist({ audience: value });
      showSnack(
        value === GA2026_AUDIENCE.ALL
          ? "公開範囲を「4期ユーザー」に変更しました。"
          : "公開範囲を「管理者のみ」に変更しました。"
      );
    } catch (err) {
      console.error("GroupActivity2026DialogAdmin audience error:", err);
      showSnack("公開範囲の更新に失敗しました。", "error");
    }
  };

  const handleToggle = async (event) => {
    const value = event.target.checked;
    setEnabled(value);
    try {
      await persist({ enabled: value });
      showSnack(
        value
          ? "ダイアログを「表示する」に変更しました。"
          : "ダイアログを「表示しない」に変更しました。"
      );
    } catch (err) {
      console.error("GroupActivity2026DialogAdmin toggle error:", err);
      showSnack("更新に失敗しました。時間をおいて再度お試しください。", "error");
    }
  };

  const handleSaveText = async () => {
    setSaving(true);
    try {
      await persist({ chipLabel, heading, body });
      showSnack("文言を保存しました。");
    } catch (err) {
      console.error("GroupActivity2026DialogAdmin save error:", err);
      showSnack("文言の保存に失敗しました。", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetText = () => {
    setChipLabel(GROUP_ACTIVITY_2026_DEFAULTS.chipLabel);
    setHeading(GROUP_ACTIVITY_2026_DEFAULTS.heading);
    setBody(GROUP_ACTIVITY_2026_DEFAULTS.body);
  };

  const handleTooltipToggle = async (event) => {
    const value = event.target.checked;
    setTooltipEnabled(value);
    try {
      await persist({ tooltipEnabled: value });
      showSnack(
        value
          ? "マイページ動線のツールチップを「表示する」に変更しました。"
          : "マイページ動線のツールチップを「表示しない」に変更しました。"
      );
    } catch (err) {
      console.error("GroupActivity2026DialogAdmin tooltip toggle error:", err);
      showSnack("更新に失敗しました。", "error");
    }
  };

  const handleSaveTooltipText = async () => {
    setSavingTooltip(true);
    try {
      await persist({ tooltipText });
      showSnack("ツールチップの文言を保存しました。");
    } catch (err) {
      console.error("GroupActivity2026DialogAdmin tooltip save error:", err);
      showSnack("ツールチップの保存に失敗しました。", "error");
    } finally {
      setSavingTooltip(false);
    }
  };

  const handleSaveDeadline = async () => {
    setSavingDeadline(true);
    try {
      await persist({ deadline: deadline || "" });
      showSnack(
        deadline
          ? `回答期日を ${deadline} に設定しました。`
          : "回答期日をクリアしました。"
      );
    } catch (err) {
      console.error("GroupActivity2026DialogAdmin deadline save error:", err);
      showSnack("期日の保存に失敗しました。", "error");
    } finally {
      setSavingDeadline(false);
    }
  };

  const handleClearDeadline = () => setDeadline("");

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          ADMIN
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase }}
        >
          2026 グループ活動ダイアログ管理
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          4期ユーザー向け「2026年グループ活動に参加するかどうか」を尋ねるダイアログの、表示ON/OFFと文言を編集します。
        </Typography>
      </Box>

      {/* 公開範囲（ダイアログ・ツールチップ・マイページのカード が連動） */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <VisibilityRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
            >
              公開範囲
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              ダイアログ・マイページ動線のツールチップ・マイページの「2026年 グループ活動 参加意思」セクションを、誰に見せるかを切り替えます。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <RadioGroup
          value={audience}
          onChange={handleAudienceChange}
        >
          <FormControlLabel
            value={GA2026_AUDIENCE.ADMIN}
            control={<Radio />}
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                管理者のみ（テスト中）
              </Typography>
            }
          />
          <FormControlLabel
            value={GA2026_AUDIENCE.ALL}
            control={<Radio />}
            label={
              <Typography sx={{ fontWeight: 700, color: textBase }}>
                4期ユーザーに公開（MM2026 支払い済み）
              </Typography>
            }
          />
        </RadioGroup>

        <Typography variant="body2" sx={{ mt: 1, color: "rgba(0,0,0,0.65)" }}>
          ・「管理者のみ」中は、`isAdmin === true` のユーザーだけにダイアログ／ツールチップ／マイページ動線が表示されます。
          <br />
          ・「4期ユーザーに公開」にすると、MM2026（4期）の支払い済み（銀行振込／クレジットカード）ユーザーにのみ表示されます。それ以外のユーザーには出ません。
          <br />
          ・管理者には、設定にかかわらず常に表示されます（プレビュー・運用のため）。
        </Typography>
      </Paper>

      {/* ON/OFF カード */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <GroupsRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
            >
              ダイアログの表示する／しない
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              「公開範囲」で対象となったユーザーのうち、未回答の人に対してダイアログを毎回（日次once）表示するかどうか。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={<Switch checked={enabled} onChange={handleToggle} color="primary" />}
          label={
            <Typography sx={{ fontWeight: 700, color: textBase }}>
              {enabled ? "現在：表示する（有効）" : "現在：表示しない（停止中）"}
            </Typography>
          }
        />
      </Paper>

      {/* 文言編集カード */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
        >
          文言の編集
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)", mb: 2 }}>
          「チップラベル」「見出し」「本文」をユーザー向けの表示に合わせて編集できます。本文は改行が反映されます。
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="チップラベル"
            value={chipLabel}
            onChange={(e) => setChipLabel(e.target.value)}
            fullWidth
            size="small"
            helperText="ダイアログ上部の小さなタグに表示される文字。"
          />
          <TextField
            label="見出し"
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            fullWidth
            size="small"
            helperText="本文上部の太字の見出し。"
          />
          <TextField
            label="本文"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={5}
            helperText="本文。改行はそのまま表示されます。"
          />
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
          <Button
            onClick={handleSaveText}
            disabled={saving}
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
            }}
          >
            {saving ? "保存中…" : "文言を保存"}
          </Button>
          <Button
            onClick={handleResetText}
            disabled={saving}
            variant="outlined"
            startIcon={<RestartAltRoundedIcon />}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
          >
            既定値に戻す（未保存）
          </Button>
        </Stack>
      </Paper>

      {/* 回答期日 */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <EventRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
            >
              回答期日
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              ダイアログとマイページに「いつまでに回答してほしいか」を表示します。
              未設定（空欄）にすると期日表示は出ません。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          <TextField
            type="datetime-local"
            label="回答期日（年月日 時:分:秒）"
            value={deadline || ""}
            onChange={(e) => setDeadline(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 1 }} // 秒単位まで選べるように
            size="small"
            sx={{ minWidth: 260 }}
            helperText="サーバ時刻（JST）基準で判定します。"
          />
          {(() => {
            const info = formatDeadlineJa(deadline);
            if (!info) {
              return (
                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.55)" }}>
                  未設定
                </Typography>
              );
            }
            return (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 800,
                  color: info.isPast ? "rgb(160,30,30)" : textBase,
                }}
              >
                プレビュー：{info.label}
                {info.isPast && "（期日を過ぎています）"}
              </Typography>
            );
          })()}
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button
            onClick={handleSaveDeadline}
            disabled={savingDeadline}
            variant="contained"
            startIcon={<SaveRoundedIcon />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
            }}
          >
            {savingDeadline ? "保存中…" : "期日を保存"}
          </Button>
          <Button
            onClick={handleClearDeadline}
            disabled={savingDeadline}
            variant="outlined"
            startIcon={<RestartAltRoundedIcon />}
            sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
          >
            クリア（未保存）
          </Button>
        </Stack>
      </Paper>

      {/* マイページ動線ツールチップ */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              background:
                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
              boxShadow:
                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <TipsAndUpdatesRoundedIcon sx={{ fontSize: 30, color: accent }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: textBase, mb: 0.5 }}
            >
              マイページ動線ツールチップ
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
              ログイン中の全ユーザーに、左サイドメニュー（PC）／ボトムナビ（モバイル）の「マイページ」上に常時表示する小さな吹き出し。
              意思の変更動線をいつでも目立たせるためのものです。
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <FormControlLabel
          control={
            <Switch
              checked={tooltipEnabled}
              onChange={handleTooltipToggle}
              color="primary"
            />
          }
          label={
            <Typography sx={{ fontWeight: 700, color: textBase }}>
              {tooltipEnabled
                ? "現在：マイページに常時ツールチップを表示する"
                : "現在：マイページに常時ツールチップを表示しない"}
            </Typography>
          }
        />

        <Box sx={{ mt: 2 }}>
          <TextField
            label="ツールチップの文言"
            value={tooltipText}
            onChange={(e) => setTooltipText(e.target.value)}
            fullWidth
            size="small"
            helperText="例：2026グループ参加の意思表示はこちら"
          />
          <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
            <Button
              onClick={handleSaveTooltipText}
              disabled={savingTooltip}
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 800,
                bgcolor: accent,
                "&:hover": { bgcolor: accent, filter: "brightness(0.95)" },
              }}
            >
              {savingTooltip ? "保存中…" : "ツールチップ文言を保存"}
            </Button>
            <Button
              onClick={() =>
                setTooltipText(GROUP_ACTIVITY_2026_DEFAULTS.tooltipText)
              }
              disabled={savingTooltip}
              variant="outlined"
              startIcon={<RestartAltRoundedIcon />}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
            >
              既定値に戻す（未保存）
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* プレビュー */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 3,
          border: `1px dashed ${border}`,
          bgcolor: "rgba(0,0,0,0.02)",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: textBase, mb: 1 }}
        >
          プレビュー
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)", mb: 1.5 }}>
          現在編集中の文言で、実際のダイアログがどう見えるかを表示します（このプレビューでは「参加する／参加しない」を押しても保存されません）。
        </Typography>
        <GroupActivity2026Dialog
          open
          chipLabel={chipLabel}
          heading={heading}
          body={body}
          deadline={deadline}
          onRespond={async () => {
            showSnack("プレビューでは回答は保存されません。", "success");
          }}
          previewMode
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.type}
          onClose={handleSnackbarClose}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
